import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { ReportService } from '../../services/report.service';
import { GroupPayrollReportKey, HourlyComparisonRow } from '../../services/report.service.types';
import {hasServerErrorCode} from '../../services/server-error-code';
import { Report } from '../../models/report.model';
import { CurrencyCode, CurrencyCodes } from '../../models/payroll-currency.model';
import { DealPayrollBreakdown, hasExactDealPayrollMemoryMatch, isDealPayrollIdentityMatch,
  isDealPayrollPeriodDiagnostic, isPlanfixDealPayroll, PayrollCalculationMethodSource } from '../../models/deal-payroll.model';
import { KarmaContent } from './karma-content.model';
import {
  CONTEXT_MENU_INITIAL_POSITION,
  CONTEXT_MENU_X_OFFSET,
  CONTEXT_MENU_Y_OFFSET,
  DAYS_OF_WEEK,
  FIRST_INDEX,
  INDEX_NOT_FOUND,
  PAYROLL_ICON_MARGIN,
  REPORT_DATE_LENGTH,
  REPORT_DATE_START_INDEX,
  REPORT_MODES,
  REPORT_RELOAD_LOADING_KEY, REPORT_RELOAD_ERROR_KEY,
  ROLE_ADMIN,
  ROLE_HEAD,
  TooltipType,
  VACATION_ERROR_MESSAGE,
  VISIBILITY_STATE,
  WORK_DAYS_ERROR_MESSAGE
} from './my-working-report.constants';
import {
  CalendarMonth,
  PayrollMonthlyRatePresentation,
  PayrollReportUser,
  ReportGroupViewModel,
  ReportSessionState,
  ReportViewModel,
  RowVisibility,
  StoredReportUser
} from './my-working-report.types';
import { trackCurrencyAmount, workScheduleTitle } from './my-working-report-collection';
import { buildCalendarMonths, isWorkingCalendarDay } from './my-working-report-calendar';
import { mapKarmaLinks } from './my-working-report-karma.mapper';
import {
  MyWorkingReportPayrollPresentation,
  payrollCalculationPresentation,
  payrollMonthlyRate
} from './my-working-report-payroll.presentation';
import { MyWorkingReportTooltipBuilder } from './my-working-report-tooltip.builder';
import {
  createMyWorkingReportBrowserAdapter,
  MyWorkingReportBrowserAdapter
} from './my-working-report-browser.adapter';
import { createMyWorkingReportSession, MyWorkingReportSession } from './my-working-report-session';
import { MyWorkingReportDataSource } from './my-working-report-data.source';
import { MyWorkingReportState } from './my-working-report-state';

const PERSONAL_REPORT_COUNT = 1;
const FINANCIAL_REPORT_STORAGE_KEY = 'Reports';
const EXCEL_DENIED_KEY = 'PAYROLL_EXCEL_DENIED';
const EXCEL_STALE_KEY = 'PAYROLL_EXCEL_STALE';
const EXCEL_ERROR_KEY = 'PAYROLL_EXCEL_ERROR';
const EXCEL_UNAVAILABLE_KEY = 'PAYROLL_EXCEL_UNAVAILABLE';
const FORBIDDEN_STATUS = 403;
const CONFLICT_STATUS = 409;
const UNAVAILABLE_STATUS = 503;
const COMPARISON_ERROR_KEY = 'PAYROLL_COMPARISON_ERROR';
const COMPARISON_REFRESH_KEY = 'PAYROLL_COMPARISON_REFRESH_REQUIRED';
const NOT_FOUND_STATUS = 404;
const RATE_MONTH_LENGTH = 7;
const EMPTY_RATE_DATE = '';
const COMPARISON_STALE_CODES = ['GROUP_REPORT_JOB_NOT_FOUND', 'GROUP_REPORT_REFRESH_REQUIRED', 'GROUP_REPORT_INPUTS_CHANGED'];

@Component({
  selector: 'app-my-working-report',
  templateUrl: './my-working-report.component.html',
  styleUrls: ['./my-working-report.component.css'],
  providers: [MyWorkingReportDataSource, MyWorkingReportState, MyWorkingReportTooltipBuilder]
})
export class MyWorkingReportComponent implements OnInit, OnDestroy {
  private readonly payrollPresentation = new MyWorkingReportPayrollPresentation();
  private readonly session: MyWorkingReportSession;
  private readonly browser: MyWorkingReportBrowserAdapter = createMyWorkingReportBrowserAdapter();
  private readonly subscriptions = new Subscription();
  private reportRequests = new Subscription();

  public showFilterDropdown = false;
  public isLoaded = false;
  public reportReloadKey: string = null;
  public excelLoading = false;
  public excelStatusKey: string = null;
  public hourlyComparisonLoading = false;
  public hourlyComparisonError: string = null;
  public hourlyComparisonNeedsRefresh = false;
  private hourlyComparisonRows: {[userId: string]: HourlyComparisonRow} = {};
  private reportGroupId: string;
  private reloadState: ReportSessionState;
  private reloadUserIds: string[] = [];
  public isEyeVisible = true;
  public visibilityState: string;
  public windowWidth: number;
  public groupName: string;
  public rowNames: RowVisibility[] = [];
  public listKarma: KarmaContent[] = [];
  public reportCurrency: CurrencyCode = CurrencyCodes.BYN;
  public userId: string;
  public user: PayrollReportUser;
  public group: ReportGroupViewModel;
  public totalVacation: number;
  public reportDateFrom: string;
  public reportDateTo: string;
  public isGroupMode = false;
  public currentMonth: string;
  public months: CalendarMonth[] = [];
  public workDaysList: string[] = [];
  public daysOfWeek = DAYS_OF_WEEK;
  public isContextMenuVisible = false;
  public isContextMenuOpen = false;
  public contextMenuContent: SafeHtml;
  public contextMenuPosition = Object.assign({}, CONTEXT_MENU_INITIAL_POSITION);
  public currentContextMenuId: string | null = null;
  public dealPayrollPreviewAmounts = this.payrollPresentation.previewAmounts;
  public canViewDealPayrollBreakdown = false;
  public readonly payrollIconMargin = PAYROLL_ICON_MARGIN;
  public readonly reportService: ReportService;
  public readonly trackByCurrency = trackCurrencyAmount;
  public readonly getTitle = workScheduleTitle;
  public readonly getTooltipContent = (
    report: ReportViewModel,
    type: TooltipType
  ): SafeHtml => this.tooltipBuilder.content(report, type);
  public readonly onMouseLeave = (): void => this.hideContextMenu();

  public get salaryRate(): PayrollMonthlyRatePresentation | null {
    if (this.isGroupMode) { return null; }
    const dealMonth = this.reportService.getCurrentDealPayrollMonth();
    const report = this.reportService.allReports.find(candidate => candidate.userID === this.userId);
    return payrollMonthlyRate(this.user, report, {
      month: this.salaryRateMonth(report, dealMonth), currency: this.reportCurrency, userId: this.userId});
  }

  private salaryRateMonth(report: Report, dealMonth: string): string {
    if (isPlanfixDealPayroll(this.user) || isPlanfixDealPayroll(report)) {
      return dealMonth;
    }
    const from = (this.reportDateFrom || EMPTY_RATE_DATE).slice(REPORT_DATE_START_INDEX, RATE_MONTH_LENGTH);
    const to = (this.reportDateTo || EMPTY_RATE_DATE).slice(REPORT_DATE_START_INDEX, RATE_MONTH_LENGTH);
    return from && from === to ? from : null;
  }

  public get internalPayrollCalculation(): boolean {
    return payrollCalculationPresentation(this.displayedPayrollSource).internal;
  }

  public get payrollCalculationLabelKey(): string {
    return payrollCalculationPresentation(this.displayedPayrollSource).labelKey;
  }

  private get displayedPayrollSource(): PayrollCalculationMethodSource {
    const reports = this.reportService.allReports;
    return !this.isGroupMode && this.isLoaded && reports.length === PERSONAL_REPORT_COUNT
      && reports[FIRST_INDEX].payrollCalculationMethod ? reports[FIRST_INDEX] : this.user;
  }

  constructor(
    private reportState: MyWorkingReportState,
    private dataSource: MyWorkingReportDataSource,
    private tooltipBuilder: MyWorkingReportTooltipBuilder
  ) {
    this.reportService = reportState.reportService;
    this.session = createMyWorkingReportSession(raw => this.hasFreshReport(raw));
    this.windowWidth = this.browser.width();
  }

  ngOnInit(): void {
    this.restoreReports();
    this.initializeSubscriptions();
  }

  private reportTarget(): string {
    const owner = this.dataSource.currentUser();
    return this.session.reportTarget(this.dataSource.routeUserId() || (owner && owner.id));
  }

  private hasFreshReport(raw: string): boolean {
    return this.reportService.hasFreshReport(raw, this.session.reportContext(this.reportTarget()));
  }

  private restoreReports(): void {
    this.resetReportPresentation();
    const state = this.session.restore();
    this.restoreReportState(state);
    this.initializeUser();
    this.reportService.getCurrentDealPayrollMonth();
    this.loadReports(state);
    if (this.session.reportsInvalidated) {
      this.reportService.clearPayrollMemory();
      this.beginReportReload(state, this.session.invalidatedReports);
    }
    if (!state.isGroupMode && state.reports.length) {
      this.getWorkDays(state.reports[FIRST_INDEX].userID);
    }
  }

  private restoreReportState(state: ReportSessionState): void {
    this.reportGroupId = state.groupId;
    this.visibilityState = state.visibilityState;
    this.isGroupMode = state.isGroupMode;
    this.groupName = state.groupName;
    this.group = state.group;
    this.reportDateFrom = state.reportDateFrom;
    this.reportDateTo = state.reportDateTo;
    this.currentMonth = state.currentMonth;
    this.reportService.reportDate = state.reportDate;
  }

  private resetReportPresentation(): void {
    this.resetReportRequests();
    this.reloadState = null;
    this.reloadUserIds = [];
    this.reportService.clearRowNames();
    this.reportService.filteredReports = [];
    Object.keys(this.dealPayrollPreviewAmounts).forEach(id => delete this.dealPayrollPreviewAmounts[id]);
    this.hideContextMenu();
    this.contextMenuContent = null;
    this.currentContextMenuId = null;
    this.workDaysList = [];
    this.months = [];
    this.user = null;
    this.totalVacation = null;
    this.closeKarmaPanel();
  }

  private resetReportRequests(): void {
    this.reportRequests.unsubscribe();
    this.reportRequests = new Subscription();
    this.reportReloadKey = null;
    this.excelLoading = false;
    this.excelStatusKey = null;
    this.hourlyComparisonLoading = false;
    this.hourlyComparisonError = null;
    this.hourlyComparisonNeedsRefresh = false;
    this.hourlyComparisonRows = {};
  }

  ngOnDestroy(): void {
    this.browser.setGlobalElementsHidden(false);
    this.subscriptions.unsubscribe();
    this.reportRequests.unsubscribe();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.windowWidth = this.browser.width();
  }

  @HostListener('window:storage', ['$event'])
  onReportStorageChange(event: StorageEvent): void {
    if (event.key === FINANCIAL_REPORT_STORAGE_KEY && event.newValue === null) { this.restoreReports(); }
  }

  applyDealPayrollBreakdown(report: ReportViewModel, breakdown: DealPayrollBreakdown): void {
    const month = this.reportService.getCurrentDealPayrollMonth();
    if (!isDealPayrollIdentityMatch(report.userID, month, breakdown)
      && !isDealPayrollPeriodDiagnostic(report.userID, breakdown)) { return; }
    if (!hasExactDealPayrollMemoryMatch(report.dealPayrollBreakdown, breakdown)) {
      const reports = this.reportService.allReports as Object as ReportViewModel[];
      this.reportService.clearPayrollMemory();
      this.beginReportReload(this.session.restore(), reports);
      return;
    }
    this.payrollPresentation.applyBreakdown(
      report, breakdown, this.reportService.getCurrentDealPayrollMonth());
  }

  private beginReportReload(state: ReportSessionState, reports: ReportViewModel[]): void {
    this.reloadState = state;
    this.reloadUserIds = reports.map(report => report.userID).filter((id, index, ids) => !!id && ids.indexOf(id) === index);
    if (!state.isGroupMode) { this.reloadUserIds = [this.userId]; }
    this.retryReportReload();
  }

  retryReportReload(): void {
    if (!this.reloadState || this.reportReloadKey === REPORT_RELOAD_LOADING_KEY) { return; }
    this.reportReloadKey = REPORT_RELOAD_LOADING_KEY;
    this.isLoaded = false;
    const owner = this.dataSource.currentUser();
    const revision = this.reportService.payrollReportRevision;
    try {
      this.reportRequests.add(this.reportState.reloadReports(this.reloadState, this.reloadUserIds).subscribe(
        reports => this.finishReportReload(reports, owner && owner.id, revision),
        () => this.rejectReportReload()));
    } catch (error) { this.rejectReportReload(); }
  }

  private finishReportReload(reports: ReportViewModel[], ownerId: string, revision: number): void {
    const owner = this.dataSource.currentUser();
    if (!owner || owner.id !== ownerId || revision !== this.reportService.payrollReportRevision) { return; }
    reports.forEach(report => {
      if (report.dealPayrollBreakdown) { this.reportService.rememberDealPayrollBreakdown(report.dealPayrollBreakdown); }
    });
    const month = this.reportService.getCurrentDealPayrollMonth();
    const compact = this.reportService.toCompactReports(reports as Object as Report[], month) as Object as ReportViewModel[];
    this.session.storeReports(compact);
    this.reportService.rememberFreshReport(compact, this.reportService.captureFreshReportContext(
      this.reloadState.reportDateFrom, this.reloadState.reportDateTo, this.reportTarget()));
    this.reportReloadKey = null;
    this.loadReports(Object.assign({}, this.reloadState, {reports: compact}));
    this.reloadState = null;
    if (!this.isGroupMode && reports.length) { this.getWorkDays(reports[FIRST_INDEX].userID); }
  }

  private rejectReportReload(): void {
    this.reportReloadKey = REPORT_RELOAD_ERROR_KEY;
    this.isLoaded = true;
  }

  onMouseEnter(event: MouseEvent, report: ReportViewModel, type: TooltipType): void {
    if (!this.isContextMenuOpen && this.tooltipBuilder.canShow(report, type)) {
      this.showContextMenu(event, report, type);
    }
  }

  showContextMenu(event: MouseEvent, report: ReportViewModel, type: TooltipType): void {
    this.contextMenuContent = this.getTooltipContent(report, type);
    this.contextMenuPosition.x = event.clientX - CONTEXT_MENU_X_OFFSET;
    this.contextMenuPosition.y = event.clientY - CONTEXT_MENU_Y_OFFSET;
    this.isContextMenuVisible = true;
    this.isContextMenuOpen = true;
    this.currentContextMenuId = report.summaryRecord.id;
  }

  hideContextMenu(): void {
    this.isContextMenuVisible = false;
    this.isContextMenuOpen = false;
  }

  isWorkingDay(day: number, monthName: string, year: number): boolean {
    return isWorkingCalendarDay({day: day, monthName: monthName, year: year}, this.workDaysList);
  }

  getWorkDays(id: string): void {
    const from = this.session.reportDateFrom();
    const to = this.session.reportDateTo();
    this.reportRequests.add(this.dataSource.workDays(id, from, to).subscribe(
      response => {
        const workDaysResponse = <HttpResponse<string[]>><Object>response;
        this.workDaysList = workDaysResponse.body || [];
        this.months = buildCalendarMonths(from, to);
      },
      error => this.browser.reportError(WORK_DAYS_ERROR_MESSAGE, error)
    ));
  }

  public getKarmaInfo(id: string, from?: string, to?: string): void {
    const fromDate = from
      ? from.substring(REPORT_DATE_START_INDEX, REPORT_DATE_LENGTH)
      : this.session.reportDateFrom();
    const toDate = to
      ? to.substring(REPORT_DATE_START_INDEX, REPORT_DATE_LENGTH)
      : this.session.reportDateTo();
    this.reportRequests.add(this.dataSource.karma(id, fromDate, toDate).subscribe(
      (response: HttpResponse<KarmaContent[]>) => {
        this.listKarma = mapKarmaLinks(response.body || []);
        this.browser.setGlobalElementsHidden(true);
      },
      () => this.listKarma = []
    ));
  }

  toggleVisibility(): void {
    this.visibilityState = this.visibilityState === VISIBILITY_STATE.SHOW
      ? VISIBILITY_STATE.HIDE : VISIBILITY_STATE.SHOW;
    this.session.storeVisibility(this.visibilityState);
  }

  rowIsHidden(name: string): boolean {
    const row = this.rowNames.find(candidate => candidate.name === name);
    return row ? row.show : false;
  }

  showHideInfo(name: string): void {
    const row = this.rowNames.find(candidate => candidate.name === name);
    if (row) {
      row.show = !row.show;
    }
  }

  showDropdown(): void {
    this.showFilterDropdown = !this.showFilterDropdown;
  }

  setEyeVisibility(visible: boolean): void {
    this.isEyeVisible = visible;
  }

  closeKarmaPanel(): void {
    this.listKarma = [];
    this.browser.setGlobalElementsHidden(false);
  }

  filter(report: Report): void {
    this.reportService.filter(report);
  }

  getReportDateFrom(): string {
    this.reportDateFrom = this.session.reportDateFrom();
    return this.reportDateFrom;
  }

  getReportDateTo(): string {
    this.reportDateTo = this.session.reportDateTo();
    return this.reportDateTo;
  }

  getExcel(): void {
    if (this.excelLoading || !this.isLoaded || this.reportReloadKey) { return; }
    this.excelLoading = true;
    this.excelStatusKey = null;
    const revision = this.reportService.payrollReportRevision;
    this.reportRequests.add(this.dataSource.excel(this.reportService.reportsMin, this.groupName).subscribe(
      response => {
        this.excelLoading = false;
        if (revision !== this.reportService.payrollReportRevision) { this.excelStatusKey = EXCEL_STALE_KEY; return; }
        try { this.browser.downloadExcel(<HttpResponse<Blob>><Object>response); }
        catch (error) { this.excelStatusKey = EXCEL_ERROR_KEY; }
      }, error => this.rejectExcel(error)));
  }

  private rejectExcel(error: HttpErrorResponse): void {
    this.excelLoading = false;
    this.excelStatusKey = error && error.status === FORBIDDEN_STATUS ? EXCEL_DENIED_KEY
      : error && error.status === CONFLICT_STATUS ? EXCEL_STALE_KEY
      : error && error.status === UNAVAILABLE_STATUS ? EXCEL_UNAVAILABLE_KEY : EXCEL_ERROR_KEY;
    if (error && error.status === CONFLICT_STATUS) {
      const reports = this.reportService.allReports as Object as ReportViewModel[];
      this.reportService.clearPayrollMemory();
      this.beginReportReload(this.session.restore(), reports);
    }
  }

  private initializeUser(): void {
    const routeId = this.dataSource.routeUserId();
    const storedUser: StoredReportUser = this.dataSource.currentUser();
    const roles = storedUser && storedUser.roles ? storedUser.roles : [];
    const userId = routeId || (storedUser ? storedUser.id : null);
    this.canViewDealPayrollBreakdown = roles.indexOf(ROLE_HEAD) !== INDEX_NOT_FOUND
      || roles.indexOf(ROLE_ADMIN) !== INDEX_NOT_FOUND
      || !!storedUser && !!storedUser.id && userId === storedUser.id;
    if (this.userId !== userId) {
      this.isEyeVisible = routeId === REPORT_MODES.GROUP;
    }
    this.userId = userId;
    if (routeId !== REPORT_MODES.GROUP) {
      this.fetchUserData();
    }
  }

  private loadReports(state: ReportSessionState): void {
    const loaded = this.reportState.loadReports(state.reports, this.payrollPresentation);
    this.rowNames = loaded.rowNames;
    this.reportCurrency = loaded.currency;
    this.isLoaded = true;
    this.payrollPresentation.applyStoredReports(
      loaded.reports, this.reportService.getCurrentDealPayrollMonth());
  }

  private initializeSubscriptions(): void {
    this.subscriptions.add(this.reportService.payrollReportsInvalidated$.subscribe(() => this.restoreReports()));
    this.subscriptions.add(this.reportState.updates().subscribe(update => {
      if (!update) {
        return;
      }
      this.restoreReports();
      this.reportState.clearUpdate();
    }));
  }

  get canCompareHourly(): boolean {
    return this.isGroupMode && this.isLoaded && !this.reportReloadKey && !this.hourlyComparisonNeedsRefresh
      && this.reportService.hasGroupHourlyComparison(this.hourlyComparisonKey());
  }

  compareHourly(): void {
    if (!this.canCompareHourly || this.hourlyComparisonLoading) { return; }
    this.hourlyComparisonLoading = true;
    this.hourlyComparisonError = null;
    this.hourlyComparisonRows = {};
    this.reportRequests.add(this.reportService.getGroupHourlyComparison(this.hourlyComparisonKey()).subscribe(rows => {
      rows.forEach(row => this.hourlyComparisonRows[row.userId] = row);
      this.hourlyComparisonLoading = false;
    }, error => {
      this.hourlyComparisonLoading = false;
      this.hourlyComparisonNeedsRefresh = error.status === NOT_FOUND_STATUS || error.status === FORBIDDEN_STATUS
        || COMPARISON_STALE_CODES.some(code => hasServerErrorCode(error, code));
      this.hourlyComparisonError = this.hourlyComparisonNeedsRefresh ? COMPARISON_REFRESH_KEY : COMPARISON_ERROR_KEY;
    }, () => this.hourlyComparisonLoading = false));
  }

  refreshComparisonReport(): void {
    if (this.hourlyComparisonNeedsRefresh) { this.reportService.invalidatePayrollReports(); }
  }

  hourlyComparisonFor(userId: string): HourlyComparisonRow {
    return this.canCompareHourly ? this.hourlyComparisonRows[userId] : null;
  }

  private hourlyComparisonKey(): GroupPayrollReportKey {
    return {groupId: this.reportGroupId, from: this.reportDateFrom, to: this.reportDateTo};
  }

  private fetchUserData(): void {
    this.reportRequests.add(this.dataSource.user(this.userId).subscribe(
      (response: HttpResponse<PayrollReportUser>) => {
        const user = response && response.body;
        if (!user) {
          return;
        }
        this.user = user;
        this.fetchVacation(this.userId);
      },
      () => this.user = null
    ));
  }

  private fetchVacation(id: string): void {
    this.reportRequests.add(this.dataSource.vacation(id).subscribe(
      response => this.totalVacation = (<HttpResponse<number>><Object>response).body,
      error => this.browser.reportError(VACATION_ERROR_MESSAGE, error)
    ));
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-working-report/my-working-report.component.ts